import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as adminApi from '../api/adminApi';
import { normalizeListResponse, pickErrorMessage } from '../lib/apiHelpers';
import { PageHeader, Card, CardBody, ErrorBanner, BackLink } from '../components/PageChrome';
import ImageUploadField from '../components/ImageUploadField';

/** Digits and at most one `.`; up to 4 digits before the decimal and 2 after (e.g. 9999.99). */
function sanitizePriceInput(raw) {
  const maxIntDigits = 4;
  const maxFracDigits = 2;
  if (raw == null || raw === '') return '';
  let s = String(raw).replace(/[^\d.]/g, '');
  const d = s.indexOf('.');
  if (d === -1) {
    return s.replace(/\D/g, '').slice(0, maxIntDigits);
  }
  let intPart = s.slice(0, d).replace(/\D/g, '').slice(0, maxIntDigits);
  const after = s.slice(d + 1).replace(/\D/g, '');
  const fracPart = after.slice(0, maxFracDigits);
  const trailingDot = d !== -1 && after.length === 0;
  if (trailingDot) return intPart ? `${intPart}.` : '0.';
  if (fracPart.length) {
    if (!intPart) intPart = '0';
    return `${intPart}.${fracPart}`;
  }
  return intPart;
}

/** Prep minutes: digits only, max 3 digits (e.g. 0–999). */
function sanitizePrepMinutesInput(raw) {
  const maxDigits = 3;
  if (raw == null || raw === '') return '';
  return String(raw).replace(/\D/g, '').slice(0, maxDigits);
}

function emptyOption() {
  return { name: '', price: '' };
}

function normalizeTagsFromItem(raw) {
  if (Array.isArray(raw)) {
    return [...new Set(raw.map((t) => String(t).trim()).filter(Boolean))];
  }
  if (typeof raw === 'string' && raw.trim()) {
    return [...new Set(raw.split(',').map((t) => t.trim()).filter(Boolean))];
  }
  return [];
}

function initialForm() {
  return {
    name: '',
    description: '',
    price: '',
    imageUrl: '',
    categoryId: '',
    isFeatured: false,
    isPopular: false,
    isVegetarian: true,
    tags: [],
    customizationOptions: [],
    estimatedPrepTimeMinutes: '15',
    isActive: true,
  };
}

export function MenuItemFormRoute() {
  const { menuItemId } = useParams();
  return <MenuItemForm key={menuItemId ?? '__new__'} />;
}

export default function MenuItemForm() {
  const { menuItemId } = useParams();
  const isNew = menuItemId == null || menuItemId === 'new';
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [tagDraft, setTagDraft] = useState('');
  const tagInputRef = useRef(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const imageFieldRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = await adminApi.listCategories({ skip: 0, limit: 200 });
        const norm = normalizeListResponse(raw, 200);
        if (!cancelled) setCategories(norm.items);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const item = await adminApi.getMenuItem(menuItemId);
        if (cancelled) return;
        setForm({
          name: item.name || '',
          description: item.description || '',
          price: sanitizePriceInput(item.price != null ? String(item.price) : ''),
          imageUrl: item.imageUrl || '',
          categoryId: item.categoryId || '',
          isFeatured: Boolean(item.isFeatured),
          isPopular: Boolean(item.isPopular),
          isVegetarian: Boolean(item.isVegetarian),
          tags: normalizeTagsFromItem(item.tags),
          customizationOptions:
            Array.isArray(item.customizationOptions) && item.customizationOptions.length
              ? item.customizationOptions.map((o) => ({
                  name: o.name || '',
                  price: sanitizePriceInput(o.price != null ? String(o.price) : ''),
                }))
              : [],
          estimatedPrepTimeMinutes: sanitizePrepMinutesInput(
            item.estimatedPrepTimeMinutes != null ? String(item.estimatedPrepTimeMinutes) : '15',
          ),
          isActive: Boolean(item.isActive),
        });
      } catch (e) {
        if (!cancelled) setError(pickErrorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isNew, menuItemId]);

  function setOpt(i, key, val) {
    setForm((f) => {
      const next = [...(f.customizationOptions || [])];
      const v = key === 'price' ? sanitizePriceInput(val) : val;
      next[i] = { ...next[i], [key]: v };
      return { ...f, customizationOptions: next };
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const imageUrl = imageFieldRef.current
        ? await imageFieldRef.current.commit()
        : form.imageUrl;
      const tags = [];
      const pushTag = (t) => {
        const x = String(t || '').trim();
        if (x && !tags.includes(x)) tags.push(x);
      };
      form.tags.forEach(pushTag);
      if (tagDraft.trim()) pushTag(tagDraft.trim());
      const customizationOptions = (form.customizationOptions || []).map((o) => ({
        name: String(o.name || '').trim(),
        price: Number(o.price) || 0,
      })).filter((o) => o.name);
      const body = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        price: Number(form.price),
        imageUrl: (imageUrl || '').trim() || undefined,
        categoryId: form.categoryId,
        isFeatured: form.isFeatured,
        isPopular: form.isPopular,
        isVegetarian: form.isVegetarian,
        tags,
        customizationOptions,
        estimatedPrepTimeMinutes: Number(form.estimatedPrepTimeMinutes) || 0,
        isActive: isNew ? true : form.isActive,
      };
      if (isNew) {
        await adminApi.createMenuItem(body);
      } else {
        await adminApi.updateMenuItem(menuItemId, body);
      }
      navigate('/menu-items');
    } catch (err) {
      setError(pickErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  return (
    <>
      <BackLink to="/menu-items">Menu items</BackLink>
      <PageHeader title={isNew ? 'New menu item' : 'Edit menu item'} />
      <ErrorBanner message={error} />
      <form onSubmit={handleSubmit}>
        <Card className="mb-3">
          <CardBody>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Name</label>
                <input className="form-control" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
              </div>
              <div className="col-md-3">
                <label className="form-label">Price</label>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  className="form-control"
                  value={form.price}
                  onChange={(e) => setForm((f) => ({ ...f, price: sanitizePriceInput(e.target.value) }))}
                  required
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Prep time (min)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  className="form-control"
                  value={form.estimatedPrepTimeMinutes}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, estimatedPrepTimeMinutes: sanitizePrepMinutesInput(e.target.value) }))
                  }
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Category</label>
                <select
                  className="form-select"
                  value={form.categoryId}
                  onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}
                  required
                >
                  <option value="" disabled>
                    Select…
                  </option>
                  {categories.map((c) => (
                    <option key={c.categoryId || c.id} value={c.categoryId || c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <ImageUploadField
                  ref={imageFieldRef}
                  label="Image"
                  folder="menu-items"
                  value={form.imageUrl}
                  onChange={(url) => setForm((f) => ({ ...f, imageUrl: url }))}
                  onUploadStateChange={setUploadingImage}
                  aspect={4 / 3}
                  outputSize={{ width: 1200, height: 900 }}
                  outputType="image/jpeg"
                  outputQuality={0.9}
                  helpText="4:3 crop (matches menu cards). Upload runs when you save. PNG/JPG/WEBP up to 10 MB."
                />
              </div>
              <div className="col-12">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
              </div>
              <div className="col-12">
                <label className="form-label" htmlFor="menu-item-tags-input">
                  Tags
                </label>
                <div
                  className="tag-chips-input form-control d-flex flex-wrap align-items-center gap-1"
                  onClick={() => tagInputRef.current?.focus()}
                  role="presentation"
                >
                  {form.tags.map((tag, i) => (
                    <span key={`${tag}-${i}`} className="tag-chip">
                      <span className="tag-chip-label">{tag}</span>
                      <button
                        type="button"
                        className="tag-chip-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          setForm((f) => ({ ...f, tags: f.tags.filter((_, j) => j !== i) }));
                        }}
                        aria-label={`Remove tag ${tag}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <input
                    ref={tagInputRef}
                    id="menu-item-tags-input"
                    type="text"
                    className="tag-chips-input-field"
                    value={tagDraft}
                    autoComplete="off"
                    placeholder={form.tags.length ? 'Add another…' : 'Type a tag, then comma or Enter'}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v.includes(',')) {
                        setTagDraft(v);
                        return;
                      }
                      const parts = v.split(',');
                      const rest = parts.pop() ?? '';
                      setForm((f) => {
                        const next = [...f.tags];
                        for (const chunk of parts) {
                          const t = chunk.trim();
                          if (t && !next.includes(t)) next.push(t);
                        }
                        return { ...f, tags: next };
                      });
                      setTagDraft(rest);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const t = tagDraft.trim();
                        if (!t) return;
                        setForm((f) => (f.tags.includes(t) ? f : { ...f, tags: [...f.tags, t] }));
                        setTagDraft('');
                        return;
                      }
                      if (e.key === 'Backspace' && tagDraft === '' && form.tags.length) {
                        e.preventDefault();
                        setForm((f) => ({ ...f, tags: f.tags.slice(0, -1) }));
                      }
                    }}
                    onBlur={() => {
                      const t = tagDraft.trim();
                      if (!t) return;
                      setForm((f) => (f.tags.includes(t) ? f : { ...f, tags: [...f.tags, t] }));
                      setTagDraft('');
                    }}
                  />
                </div>
                <div className="form-text">Press comma or Enter after each tag. Backspace on an empty field removes the last tag.</div>
              </div>
              <div className="col-12">
                <label className="form-label">Visibility and flags</label>
                <div className="d-flex flex-column gap-2">
                  <div className="form-check form-switch mb-0 admin-category-switch">
                    <input
                      id="f-pop"
                      type="checkbox"
                      role="switch"
                      className="form-check-input"
                      checked={form.isPopular}
                      onChange={(e) => setForm((f) => ({ ...f, isPopular: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="f-pop">
                      Popular
                    </label>
                  </div>
                  <div className="form-check form-switch mb-0 admin-category-switch">
                    <input
                      id="f-feat"
                      type="checkbox"
                      role="switch"
                      className="form-check-input"
                      checked={form.isFeatured}
                      onChange={(e) => setForm((f) => ({ ...f, isFeatured: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="f-feat">
                      Featured
                    </label>
                  </div>
                  <div className="form-check form-switch mb-0 admin-category-switch">
                    <input
                      id="f-veg"
                      type="checkbox"
                      role="switch"
                      className="form-check-input"
                      checked={form.isVegetarian}
                      onChange={(e) => setForm((f) => ({ ...f, isVegetarian: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="f-veg">
                      Vegetarian
                    </label>
                  </div>
                </div>
                {isNew ? (
                  <p className="small text-secondary mt-2 mb-0">New menu items are saved as active.</p>
                ) : null}
              </div>
            </div>
          </CardBody>
        </Card>

        <Card className="mb-3">
          <CardBody>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h2 className="h6 mb-0">Customization options</h2>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => setForm((f) => ({ ...f, customizationOptions: [...f.customizationOptions, emptyOption()] }))}
              >
                Add option
              </button>
            </div>
            {(form.customizationOptions || []).length === 0 ? (
              <p className="text-secondary small mb-0">No add-ons. Use “Add option” for extras like cheese or spice.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th style={{ width: 140 }}>Price</th>
                      <th style={{ width: 80 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {form.customizationOptions.map((o, i) => (
                      <tr key={i}>
                        <td>
                          <input className="form-control form-control-sm" value={o.name} onChange={(e) => setOpt(i, 'name', e.target.value)} />
                        </td>
                        <td>
                          <input
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            className="form-control form-control-sm"
                            value={o.price}
                            onChange={(e) => setOpt(i, 'price', e.target.value)}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() =>
                              setForm((f) => ({
                                ...f,
                                customizationOptions: f.customizationOptions.filter((_, j) => j !== i),
                              }))
                            }
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>

        <div className="d-flex gap-2">
          <button type="submit" className="btn btn-primary" disabled={saving || uploadingImage}>
            {uploadingImage ? 'Uploading image…' : saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/menu-items')} disabled={saving}>
            Cancel
          </button>
        </div>
      </form>
    </>
  );
}
